import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useRidesLookupsService,
  type RidesLookupsView,
  type RidesCampusView,
  type RidesEventView,
  type RidesServiceTimeView,
  type RidesLookupItem,
} from '@actschurch/ccms-sdk';
import { ridesKeys } from './queryKeys';
import { todayYmd, toYmd } from './format';

/** Lookups + campuses + public settings. Cached for 10 minutes; safe to call from any screen. */
export function useRidesLookups() {
  const lookups = useRidesLookupsService();
  return useQuery<RidesLookupsView>({
    queryKey: ridesKeys.lookups,
    queryFn: () => lookups.getLookups(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/** Upcoming ride events (default: today → +settings.eventWindowDays, 120 until settings load). */
export function useRidesEvents(from?: string | null, to?: string | null) {
  const lookups = useRidesLookupsService();
  const { data: lk } = useRidesLookups();
  const windowDays = Math.max(1, lk?.settings?.eventWindowDays || 120);
  const f = from ?? todayYmd();
  const t = useMemo(() => {
    if (to) return to;
    const d = new Date();
    d.setDate(d.getDate() + windowDays);
    return toYmd(d);
  }, [to, windowDays]);
  return useQuery<RidesEventView[]>({
    queryKey: ridesKeys.events(f, t),
    queryFn: () => lookups.getEvents({ from: f, to: t }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function campusById(lookups: RidesLookupsView | undefined, id: number | null | undefined): RidesCampusView | undefined {
  if (!lookups || id == null) return undefined;
  return lookups.campuses.find((c) => c.locationID === id);
}

export function campusAddress(c: RidesCampusView | undefined): string {
  if (!c) return '';
  return [c.addressLine, c.suburb, c.city].filter(Boolean).join(', ');
}

export function serviceTimesFor(c: RidesCampusView | undefined): RidesServiceTimeView[] {
  return c?.serviceTimes ?? [];
}

export function serviceTimeById(c: RidesCampusView | undefined, id: number | null | undefined): RidesServiceTimeView | undefined {
  if (!c || id == null) return undefined;
  return c.serviceTimes.find((t) => t.serviceTimeID === id);
}

export function lookupName(items: RidesLookupItem[] | undefined, id: number | null | undefined): string {
  if (!items || id == null) return '';
  return items.find((i) => i.id === id)?.name ?? '';
}

/**
 * Mantine Select data for the licence code lookup ("B: Light motor vehicle", or just "B" when the name is the code). When
 * `allowedCodes` (settings.allowedLicenceCodes) is given, only those codes are offered.
 */
export function licenceCodeOptions(items: RidesLookupItem[] | undefined, allowedCodes?: string[]) {
  const allow = allowedCodes?.length ? new Set(allowedCodes.map((c) => c.toUpperCase())) : null;
  return (items ?? [])
    .filter((i) => i.isActive)
    .filter((i) => !allow || (i.code ? allow.has(i.code.toUpperCase()) : false))
    .map((i) => ({ value: String(i.id), label: i.code ? (i.name && i.name !== i.code ? `${i.code}: ${i.name}` : i.code) : i.name }));
}

/** Lookup ids of the licence codes in `allowedCodes` (settings.allowedLicenceCodes). */
export function licenceCodeIdsFor(items: RidesLookupItem[] | undefined, allowedCodes: string[]): number[] {
  const allow = new Set(allowedCodes.map((c) => c.toUpperCase()));
  return (items ?? []).filter((i) => i.code && allow.has(i.code.toUpperCase())).map((i) => i.id);
}

/** Mantine Select data for countries (alpha-3 value). */
export function countryOptions(lookups: RidesLookupsView | undefined) {
  return (lookups?.countries ?? []).map((c) => ({ value: c.code, label: c.name }));
}
